import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { Subscription, debounceTime, startWith } from 'rxjs';
import { AdminsApi, ImpersonationTarget, PagedImpersonationTargetResponse } from 'src/app/hickory-api';
import { AuthService } from '../authentication/auth.service';
import { ApiError, isInteger, toInteger } from '../model/utility';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmComponent, ConfirmData } from '../modals/confirm/confirm.component';

export interface ImpersonationInfo {
  redirectUrl?: string,
  sourceAdminId?: string,
  targetAdminId?: string,
}

@Component({
  selector: 'app-impersonation',
  templateUrl: './impersonation.component.html',
  styleUrls: ['./impersonation.component.scss'],
  standalone: false
})
export class ImpersonationComponent implements OnInit, OnDestroy {
  // table
  lastResponse: PagedImpersonationTargetResponse;
  lastError: string;
  dataSource = new MatTableDataSource<ImpersonationTarget>();
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  get displayedColumns(): string[] { return ['name', 'email', 'tenant']; }
  get pageSize() { return Math.max(this.paginator.pageSize ?? 10, 10); }
  get page() { return this.lastResponse?.page ?? 1; }

  // search
  private lastRequest$: Subscription;
  searchControl = new UntypedFormControl();
  searchQuery: string;
  requestInProgress: boolean = false;

  subscription: Subscription = new Subscription();

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private translation: L10nTranslationService,
    private authService: AuthService,
    private adminsApi: AdminsApi,
    private dialog: MatDialog,
  ) { }

  ngOnInit() {
    this.subscription.add(this.searchControl.valueChanges.pipe(startWith(''), debounceTime(500)).subscribe(newValue => this.onQueryChanged(newValue)));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private onQueryChanged(newValue: string) {
    const query = newValue || null;
    this.searchQuery = query;
    this.fetchData(1);
  }

  fetchData(page: number = this.page) {
    const query = this.searchQuery?.trim();
    this.lastRequest$?.unsubscribe();
    if ((query?.length ?? 0) <= 3) return;
    this.requestInProgress = true;
    this.lastError = null;
    const ids = isInteger(query) ? [toInteger(query)] : null;
    const search = isInteger(query) ? null : query;
    const sub$ = this.adminsApi.getImpersonationTargets(ids, search, page, this.pageSize, 'lastName,firstName').subscribe({
      next: (res) => {
        this.lastResponse = res;
        this.dataSource = new MatTableDataSource(res.items);
        this.requestInProgress = false;
      },
      error: (err: ApiError) => {
        this.lastResponse = null;
        let message = this.translation.translate('ErrorLoadingImpersonationAdmins');
        if (err.statusText) message += `: ${err.statusText}`;
        this.dataSource = new MatTableDataSource();
        this.requestInProgress = false;
        this.lastError = message;
      },
    });
    this.lastRequest$ = sub$;
    this.subscription.add(sub$);
  }

  resetSearch() {
    this.lastRequest$?.unsubscribe();
    this.searchControl.reset();
    this.searchQuery = null;
    this.lastResponse = null;
    this.requestInProgress = false;
  }

  getTooltip(target: ImpersonationTarget): string {
    return `Admin ID: ${target?.id ?? '??'}; Tenant ID: ${target?.tenantId ?? '??'}`;
  }

  getDisplayName(target: ImpersonationTarget): string {
    return (target?.name?.familyName ?? '') + ', ' + (target?.name?.givenName);
  }

  onPageEvent(event: PageEvent) {
    const nextPage = event.pageIndex + 1;
    this.fetchData(nextPage);
  }

  onSelectionChanged(selection: ImpersonationTarget) {
    if (!selection) return;
    const data: ConfirmData = { title: 'Impersonation', message: `Begin impersonation as ${this.getDisplayName(selection)} (${selection.id})?` };
    this.dialog.open(ConfirmComponent, { data: data }).afterClosed().subscribe(result => {
      if (result) this.onImpersonationConfirmed(selection);
    })
  }

  onImpersonationConfirmed(target: ImpersonationTarget) {
    const adminId = target?.id?.toString();
    this.authService.startImpersonation(adminId)
  }
}
